import React from "react";
import baseContentLayout from "../shared/base-content/index";
import "./index.scss";
import MyButton from "../shared/buttons/primary";
import ReactPaginate from "react-paginate";
import CustomDatePicker from "../shared/form-datepicker";
import moment from "moment";
import { AVAILABLE_SLOT } from "../../constants/app";
import ConfirmModal from "./dialog/confirm";
import BookingDetail from "./dialog/booking-detail";

const Content = (props) => {
  const LIMIT = 20;
  const [listTable, setListTable] = React.useState([]);
  const [keyword, setKeyword] = React.useState("");
  const [currentPage, setCurrentPage] = React.useState(1);
  const [resetPagination, setResetPagination] = React.useState(0);
  const [isReset, setIsReset] = React.useState(0);
  const [dateFilter, setDateFilter] = React.useState(new Date());
  const [isLoading, setIsLoading] = React.useState(true);
  const [showBookingDetail, setShowBookingDetail] = React.useState({
    isShow: false,
    bookingId: "",
  });
  const [confirmModal, setConfirmModal] = React.useState({
    message: "",
    isShow: false,
    modalType: "",
    acceptValue: "",
    tableId: "",
  });

  React.useEffect(() => {
    reloadAvailableTable(true);
  }, [isReset, currentPage]);

  const reloadAvailableTable = () => {
    window
      .axios({
        url: `/shop/${props.auth.user.id}/table/`,
        method: "get",
        params: {
          name: keyword.trim(),
          date: dateFilter,
          page: currentPage,
          page_size: LIMIT,
          booking: true,
        },
      })
      .then((result) => {
        const data = result.data;
        setListTable(data);
        if (isLoading) {
          setIsLoading(false);
        }
      });
  };

  const handleSearch = () => {
    setCurrentPage(1);
    setResetPagination(resetPagination + 1);
    setIsReset(isReset + 1);
  };

  const handlePageClick = ({ selected }) => {
    setCurrentPage(+selected + 1);
    reloadAvailableTable(true);
  };

  const renderPagination = () => {
    const showPagination = listTable.count > LIMIT;
    if (!showPagination) return null;
    return (
      <ReactPaginate
        key={resetPagination}
        selectedPageRel={currentPage}
        previousLabel={<span className="material-icons">chevron_left</span>}
        nextLabel={<span className="material-icons">chevron_right</span>}
        disabledClassName={"navigation-disabled"}
        breakLabel={"..."}
        breakClassName={"break-me"}
        pageCount={Math.ceil(listTable.count / LIMIT) || 1}
        marginPagesDisplayed={2}
        pageRangeDisplayed={5}
        onPageChange={handlePageClick}
        containerClassName={"my-pagination"}
        subContainerClassName={"pages pagination"}
        activeClassName={"active"}
      />
    );
  };

  const renderEmptyData = () => {
    return (
      <tr>
        <td colSpan="5">テーブルが有りません</td>
      </tr>
    );
  };

  const toMinutes = (timeStr) => {
    const [h, m] = timeStr.split(":").map(Number);
    return h * 60 + m;
  };

  const toTimeStr = (minutes) => {
    const h = String(Math.floor(minutes / 60)).padStart(2, "0");
    const m = String(minutes % 60).padStart(2, "0");
    return `${h}:${m}`;
  };

  const groupUnAvailableSlots = (slots) => {
    const resultMap = {};
    const unBookingId = [];
    slots.forEach((item) => {
      const { booking_id, start_time, end_time } = item;
      if (booking_id) {
        if (!resultMap[booking_id]) {
          resultMap[booking_id] = { ...item };
        } else {
          const currentStart = toMinutes(resultMap[booking_id].start_time);
          const currentEnd = toMinutes(resultMap[booking_id].end_time);
          const newStart = toMinutes(start_time);
          const newEnd = toMinutes(end_time);

          resultMap[booking_id].start_time = toTimeStr(
            Math.min(currentStart, newStart)
          );
          resultMap[booking_id].end_time = toTimeStr(
            Math.max(currentEnd, newEnd)
          );
        }
      } else {
        unBookingId.push(item);
      }
    });

    return [...unBookingId, ...Object.values(resultMap)];
  };

  const splitAvailableSlots = React.useCallback((slots) => {
    const available = [];
    let unAvailable = [];
    slots?.forEach((item) => {
      (item.status === AVAILABLE_SLOT ? available : unAvailable).push(item);
    });
    unAvailable = groupUnAvailableSlots(unAvailable);
    return { available, unAvailable };
  }, []);

  const SlotElement = React.memo(({ tableId, slots, variant }) => {
    return (
      <div className="slots">
        {slots.map((slot) => {
          return (
            <MyButton
              key={`${slot.start_time}_${slot.end_time}`}
              className="me-2 mb-2"
              myVariant={variant}
              onClick={() => handleShowConfirmModal(tableId, slot.status, slot)}
            >
              {slot.start_time} - {slot.end_time}
            </MyButton>
          );
        })}
      </div>
    );
  });

  const TableRow = React.memo(({ item }) => {
    const { available, unAvailable } = splitAvailableSlots(
      item.available_slots
    );
    return (
      <tr key={item.id}>
        <td>
          {window._.truncate(item.name, {
            length: 30,
            omission: "...",
          })}
        </td>
        <td>{item.seats ? `${item.seats}席` : ""}</td>
        <td>
          {window._.truncate(item.detail, {
            length: 80,
            omission: "...",
          })}
        </td>
        <td className="buttons">
          <SlotElement tableId={item.id} slots={available} variant="edit" />
        </td>
        <td className="buttons">
          <SlotElement tableId={item.id} slots={unAvailable} variant="delete" />
        </td>
      </tr>
    );
  });

  const renderTableRows = React.useMemo(() => {
    const tables = listTable.results;
    if (!tables?.length) return renderEmptyData();
    return tables.map((item) => <TableRow key={item.id} item={item} />);
  }, [listTable]);

  const handleCloseConfirmModal = () => {
    setConfirmModal({
      message: "",
      isShow: false,
      modalType: "",
      acceptValue: "",
    });
  };

  const handleShowConfirmModal = (tableId, modalType, slot) => {
    const hasBooking = slot.booking_id;
    const isAvailable = modalType === AVAILABLE_SLOT;
    if (!isAvailable && hasBooking) {
      setShowBookingDetail({
        bookingId: slot.booking_id,
        isShow: true,
      });
    } else {
      const message = isAvailable
        ? "この時間枠のステータスを『予約済み』に変更しますか？"
        : "この時間枠のステータスを『未予約』に変更しますか？";
      const formattedDate = moment(dateFilter).format("YYYY-MM-DD");
      const acceptValue = moment(
        `${formattedDate} ${slot.start_time}`,
        "YYYY-MM-DD HH:mm"
      ).format("YYYY-MM-DD HH:mm");
      setConfirmModal({
        ...confirmModal,
        message,
        isShow: true,
        modalType,
        acceptValue,
        tableId,
      });
    }
  };

  const handleAcceptConfirmModal = () => {
    let data = {
      start_time: confirmModal.acceptValue,
      table_id: confirmModal.tableId,
    };

    if (confirmModal.modalType !== AVAILABLE_SLOT) {
      data["unblock"] = true;
    }

    window
      .axios({
        method: "post",
        url: `shop/${props.auth.user.id}/block_time/`,
        data,
      })
      .then(() => {
        reloadAvailableTable();
        handleCloseConfirmModal();
      });
  };

  const handleCloseDetailBookingModal = (data) => {
    if (data) {
      reloadAvailableTable();
    }

    setShowBookingDetail({
      bookingId: "",
      isShow: false,
    });
  };

  return (
    <div className="table-available-table">
      <div className="table-available-table__search mb-4 d-flex align-items-center">
        <div className="me-3">
          <div className="table__search">
            <div className="form-group d-inline-block me-3">
              <input
                type="text"
                className="form-control no-box-shadow d-inline-block"
                placeholder="テーブル名"
                value={keyword}
                onChange={(e) => {
                  const { value } = e.target;
                  setKeyword(value);
                }}
                onKeyUp={(event) => {
                  if (event.key === "Enter") {
                    handleSearch();
                  }
                }}
              />
            </div>
            <div className="form-group d-inline-block me-3">
              <CustomDatePicker
                className="form-control"
                option={{
                  selectedDate: dateFilter ? moment(dateFilter).toDate() : null,
                  placeholder: "YYYY年MM月DD日",
                }}
                dateFormat="yyyy年MM月dd日"
                onChange={(date) => {
                  setDateFilter(moment(date).format("YYYY-MM-DD"));
                }}
              />
            </div>

            <MyButton myVariant="search" onClick={handleSearch}>
              検索
            </MyButton>
          </div>
        </div>
      </div>
      {!isLoading ? (
        <div className="history__table table-available-table__table">
          <table className="table">
            <thead className="">
              <tr>
                <th scope="col" className="col-username">
                  テーブル名
                </th>
                <th scope="col" className="col-seat">
                  席数
                </th>
                <th scope="col" className="col-detail">
                  備考
                </th>
                <th scope="col" className="col-activity">
                  空き時間
                </th>
                <th scope="col" className="col-activity">
                  満席時間
                </th>
              </tr>
            </thead>
            <tbody>{renderTableRows}</tbody>
          </table>
          {renderPagination()}
        </div>
      ) : (
        <div className="loader-wrapper">
          <div className="loader">Loading...</div>
        </div>
      )}

      {confirmModal.isShow && (
        <ConfirmModal
          message={confirmModal.message}
          onClose={handleCloseConfirmModal}
          onAccept={handleAcceptConfirmModal}
        />
      )}
      {showBookingDetail.isShow && (
        <BookingDetail
          {...showBookingDetail}
          onClose={handleCloseDetailBookingModal}
        />
      )}
    </div>
  );
};

const CustomerContent = baseContentLayout(Content);

const AvailableTable = () => {
  const auth = window.store.getState().auth;
  return <CustomerContent name="空席管理画面" auth={auth} />;
};

export default AvailableTable;
